import { createSelector } from '@reduxjs/toolkit';

import LoadingStatus from '@libs/loading-status';

import ContractAuthenticationStatus from '@app/store/contract-authentication/ContractAuthenticationStatus';
import { RootState } from '@app/store/types';

export const getContractAuthenticationState = (state: RootState) => state.contractAuthentication;

export const getAuthentications = (state: RootState) =>
  getContractAuthenticationState(state).authentications;

export const getGuideCheckLoadingStatus = (state: RootState) =>
  getContractAuthenticationState(state).guideCheckLoadingStatus;

export const getLoadingStatus = (state: RootState) =>
  getContractAuthenticationState(state).loadingStatus;

export const isMessageReadSelector = (state: RootState) =>
  getContractAuthenticationState(state).isMessageRead;

export const sequencesSelector = (state: RootState) =>
  getContractAuthenticationState(state).sequences;

export const getAuthenticationKey = (state: RootState) =>
  getContractAuthenticationState(state).authenticationKey;

export const getDocumentId = (state: RootState) => getContractAuthenticationState(state).documentId;

export const getParticipantId = (state: RootState) =>
  getContractAuthenticationState(state).participantId;

export const getContractAuthenticationList = (state: RootState) =>
  // 추가 인증 수단이 없는 경우 null 이라서 null이 아닌 인증수단만 반환합니다.
  Object.values(getAuthentications(state) ?? {}).filter((auth) => !!auth);

export const getIsExceed = createSelector(getContractAuthenticationList, (authList): boolean => {
  return authList.some((auth) => auth.failureCount >= 5 && !!auth.retryAt);
});

/**
 * 접근암호인증과 관련된 정보를 반환한다.
 */
export const getPasswordAuthentication = createSelector(getAuthentications, (authentications) => {
  return authentications.password;
});

/**
 * 휴대폰본인인증과 관련된 정보를 반환한다.
 */
export const getPhoneAuthentication = createSelector(getAuthentications, (authentications) => {
  return authentications.mobile;
});

export const getPhoneAuthenticationDetailStatus = createSelector(
  getPhoneAuthentication,
  (mobile) => {
    return mobile?.authenticationDetailStatus;
  }
);

export const getPhoneAuthenticationErrorMessage = createSelector(
  getPhoneAuthentication,
  (mobile) => {
    return mobile?.errorMessage;
  }
);

export const getPhoneAuthenticationLeftFailureCount = createSelector(
  getPhoneAuthentication,
  (mobile) => {
    return mobile?.leftFailureCount;
  }
);

/**
 * 법인 공동 인증서 관련된 정보를 반환한다.
 */
export const getDigitalCertificateAuthentication = createSelector(
  getAuthentications,
  (authentications) => {
    return authentications.digitalCertificate;
  }
);

export const getIsDigitalCertificateAuthenticated = createSelector(
  getDigitalCertificateAuthentication,
  getLoadingStatus,
  (digitalCertificate, loadingStatus) => {
    return (
      (loadingStatus === LoadingStatus.SUCCEEDED && !digitalCertificate) ||
      digitalCertificate?.status === ContractAuthenticationStatus.SUCCEEDED
    );
  }
);

export const isPasswordAuthenticatedSelector = createSelector(
  getPasswordAuthentication,
  getLoadingStatus,
  (passwordAuth, loadingStatus) => {
    return (
      (loadingStatus === LoadingStatus.SUCCEEDED && !passwordAuth) ||
      passwordAuth?.status === ContractAuthenticationStatus.SUCCEEDED
    );
  }
);

export const isPhoneAuthenticatedSelector = createSelector(
  getPhoneAuthentication,
  getLoadingStatus,
  (mobileAuth, loadingStatus) => {
    return (
      (loadingStatus === LoadingStatus.SUCCEEDED && !mobileAuth) ||
      mobileAuth?.status === ContractAuthenticationStatus.SUCCEEDED
    );
  }
);

export const isPhoneNumberMatchedSelector = createSelector(getPhoneAuthentication, (mobileAuth) => {
  return !!mobileAuth?.phoneNumberLastDigits;
});

/**
 * 휴대폰 본인인증과 관련된 휴대폰 앞자리(뒷번호 4자리 제외)를 반환한다.
 */
export const getPhoneNumberPrefix = createSelector(
  getPhoneAuthentication,
  (phoneAuthentication) => phoneAuthentication?.phoneNumber
);

export const getHasRequestedAuthentication = createSelector(
  getAuthentications,
  ({ digitalCertificate, mobile, password }) => !!(digitalCertificate || mobile || password)
);

/**
 * 필요한 모든 인증이 완료되었는 지 반환한다.
 */
export const isAllAuthenticated = createSelector(
  getIsDigitalCertificateAuthenticated,
  isPasswordAuthenticatedSelector,
  isPhoneAuthenticatedSelector,
  (isDigitalCertificateAuthenticated, isPasswordAuthenticated, isPhoneAuthenticated) =>
    isDigitalCertificateAuthenticated && isPasswordAuthenticated && isPhoneAuthenticated
);

/**
 * 재시도 가능한 시간을 반환한다.
 */

// TODO(@Gilhyeon): 인증수단별로 어떻게 사용하는지 검토해보기
export const getRetryTime = createSelector(getContractAuthenticationList, (authList) => {
  if (authList) {
    const needRetryAuth = authList.find((v) => !!v.retryAt);
    return needRetryAuth ? needRetryAuth.retryAt : null;
  }
  return null;
});
